import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { hotelsAPI, activitiesAPI } from '../services/api';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import { useSelectedActivities } from './SelectedActivitiesContext';

const Hotels = ({ initialCategory = 'all', hideCategoryFilters = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedImage, setSelectedImage] = useState(0);
  const [hotelsData, setHotelsData] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activities, setActivities] = useState([]);
  const { selectedActivities, toggleActivity, setSelectedActivities, isActivitySelected } = useSelectedActivities();
  const [showActivitySidebar, setShowActivitySidebar] = useState(false);

  useEffect(() => {
    fetchHotels();
    fetchActivities();
  }, []);

  useEffect(() => {
    // Determine category: URL param has priority, else prop, else 'all'
    const categoryFromUrl = searchParams.get('category');
    const categoryToUse = (categoryFromUrl || initialCategory || 'all');
    setSelectedCategory(categoryToUse);
    if (hotelsData && hotelsData.length > 0) {
      filterHotelsByCategory(categoryToUse);
    } else if (hotelsData && hotelsData.length === 0) {
      setFilteredHotels([]);
    }
  }, [searchParams, hotelsData, initialCategory]);

  const fetchActivities = async () => {
    try {
      const response = await activitiesAPI.getAll();
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchHotels = async () => {
    try {
      setError('');
      const response = await hotelsAPI.getAll();
      console.log('Hotels API Response:', response);
      console.log('Hotels Data:', response.data);
      // Handle both array and object response
      const hotels = Array.isArray(response.data) ? response.data : (response.data?.hotels || []);
      // Normalize the data to ensure it has both _id and id, and default category
      const normalizedData = hotels.map(hotel => ({
        ...hotel,
        id: hotel.id || hotel._id,
        _id: hotel._id || hotel.id,
        category: hotel.category || 'hotel',
        images: hotel.images && hotel.images.length > 0 ? hotel.images : ['https://via.placeholder.com/200x150'],
        rating: hotel.rating || 0,
        totalRatings: hotel.totalRatings || 0,
        ratingText: hotel.ratingText || 'Good',
        tags: hotel.tags || [],
        amenities: hotel.amenities || [],
        originalPrice: hotel.originalPrice || 0,
        currentPrice: hotel.currentPrice || 0,
        taxes: hotel.taxes || 0
      }));
      console.log('Normalized Hotels Data:', normalizedData);
      setHotelsData(normalizedData);
    } catch (error) {
      console.error('Error fetching hotels:', error);
      setError('Unable to load hotels. Please check your connection and try again.');
      setHotelsData([]);
    } finally {
      setLoading(false);
    }
  };

  const filterHotelsByCategory = (category) => {
    if (!hotelsData || hotelsData.length === 0) {
      setFilteredHotels([]);
      return;
    }

    if (category === 'all') {
      setFilteredHotels(hotelsData);
    } else if (category.includes(',')) {
      // Handle multiple categories (e.g., "villa,homestay")
      const categories = category.split(',').map(cat => cat.trim().toLowerCase());
      const filtered = hotelsData.filter(hotel => {
        const hotelCategory = hotel.category ? hotel.category.toLowerCase() : 'hotel';
        return categories.includes(hotelCategory);
      });
      setFilteredHotels(filtered);
    } else {
      const filtered = hotelsData.filter(hotel => {
        const hotelCategory = hotel.category ? hotel.category.toLowerCase() : 'hotel';
        return hotelCategory === category.toLowerCase();
      });
      setFilteredHotels(filtered);
    }
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    filterHotelsByCategory(category);
    
    // Update URL with selected category
    const newSearchParams = new URLSearchParams(searchParams);
    if (category === 'all') {
      newSearchParams.delete('category');
    } else {
      newSearchParams.set('category', category);
    }
    setSearchParams(newSearchParams);
  };

  const handleActivityToggle = (activityId) => toggleActivity(activityId);

  const handleSelectAllActivities = () => {
    if (selectedActivities.length === activities.length) {
      setSelectedActivities([]);
    } else {
      setSelectedActivities(activities.map(activity => activity._id));
    }
  };

      if (loading) {
        return (
          <section className="section" id="hotels">
            <div className="container">
              <h2 className="section-title">Featured Accommodation</h2>
              <LoadingSpinner text="Loading hotels..." />
            </div>
          </section>
        );
      }

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star" style={{color: '#FFD700'}}></i>);
    }

    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt" style={{color: '#FFD700'}}></i>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star" style={{color: '#FFD700'}}></i>);
    }

    return stars;
  };

      return (
        <section className="section" id="hotels">
          <div className="container">
            <h2 className="section-title">
              {selectedCategory === 'all' ? 'Accommodation' : 
               selectedCategory === 'hotel' ? 'Hotels' :
               selectedCategory === 'villa' ? 'Villas' :
               selectedCategory === 'homestay' ? 'Homestays' : 
               selectedCategory === 'villa,homestay' ? 'HomeStay & Villas' : 'Accommodation'}
            </h2>
            
            {/* Category Filter Buttons */}
            {!hideCategoryFilters && (
              <div className="category-filters">
                <button 
                  className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter('all')}
                >
                  All Properties
                </button>
                <button 
                  className={`filter-btn ${selectedCategory === 'hotel' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter('hotel')}
                >
                  Hotels
                </button>
                <button 
                  className={`filter-btn ${selectedCategory === 'homestay' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter('homestay')}
                >
                  Homestay
                </button>
                <button 
                  className={`filter-btn ${selectedCategory === 'villa' ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter('villa')}
                >
                  Villas
                </button>
              </div>
            )}

            {error && (
              <ErrorMessage 
                message={error} 
                type="error" 
                onClose={() => setError('')}
              />
            )}

            {filteredHotels.length === 0 && !loading && (
              <div className="no-results">
                <p>No {selectedCategory === 'all' ? 'properties' : selectedCategory + 's'} found.</p>
              </div>
            )}

            <div className="hotels-layout">
              {/* Activity Sidebar - Always Visible */}
              <div className="activity-sidebar">
                  <div className="activity-header">
                    <h3>Available Activities</h3>
                    <button 
                      className="select-all-btn"
                      onClick={handleSelectAllActivities}
                    >
                      {selectedActivities.length === activities.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  
                  <div className="activity-list">
                    {activities.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                        <p>No activities available at the moment.</p>
                      </div>
                    ) : (
                      <>
                        {/* Selected Activities at the Top */}
                        {selectedActivities.length > 0 && (
                          <>
                            {selectedActivities.map(activityId => {
                              const activity = activities.find(a => String(a._id) === String(activityId));
                              if (!activity) return null;
                              return (
                                <div key={`selected-${activityId}`} className="activity-item activity-item-selected">
                                  <div className="activity-item-content">
                                    <input
                                      type="checkbox"
                                      id={`activity-${activityId}`}
                                      checked={true}
                                      onChange={() => handleActivityToggle(activityId)}
                                      className="activity-checkbox-input"
                                    />
                                    <label htmlFor={`activity-${activityId}`} className="activity-item-label">
                                      <span className="activity-title">{activity.activityName}</span>
                                      <span className="activity-price">₹{activity.costing}</span>
                                    </label>
                                  </div>
                                </div>
                              );
                            })}
                            {/* Separator */}
                            <div className="activity-separator"></div>
                          </>
                        )}
                        
                        {/* Unselected Activities */}
                        {activities
                          .filter(activity => !isActivitySelected(activity._id))
                          .map((activity) => (
                            <div key={activity._id} className="activity-item">
                              <div className="activity-item-content">
                                <input
                                  type="checkbox"
                                  id={`activity-${activity._id}`}
                                  checked={isActivitySelected(activity._id)}
                                  onChange={() => handleActivityToggle(activity._id)}
                                  className="activity-checkbox-input"
                                />
                                <label htmlFor={`activity-${activity._id}`} className="activity-item-label">
                                  <span className="activity-title">{activity.activityName}</span>
                                  <span className="activity-price">₹{activity.costing}</span>
                                </label>
                              </div>
                            </div>
                          ))
                        }
                      </>
                    )}
                  </div>
                </div>

              {/* Hotels List */}
              <div className="hotels-list">
                {filteredHotels.map((hotel) => (
            <div key={hotel.id || hotel._id} className="hotel-card-detailed">
              {/* Left Section - Single Image */}
              <div className="hotel-images-section">
                <img 
                  src={hotel.images && hotel.images[0] ? hotel.images[0] : 'https://via.placeholder.com/200x150'} 
                  alt={hotel.name}
                  className="hotel-main-image"
                />
              </div>

              {/* Middle Section - Details */}
              <div className="hotel-details-section">
                <div className="hotel-header">
                  <h3 className="hotel-name">{hotel.name}</h3>
                  <div className="hotel-rating-inline">
                    <div className="stars">
                      {renderStars(hotel.rating)}
                    </div>
                    <span className="rating-text">{hotel.ratingText}</span>
                    <span className="rating-count">({hotel.totalRatings} Ratings)</span>
                  </div>
                </div>

                <div className="hotel-location">
                  <span className="location-name">{hotel.location}</span>
                  <span className="location-detail">{hotel.locationDetail}</span>
                  {hotel.category && (
                    <span className="category-badge">{hotel.category}</span>
                  )}
                </div>

                {hotel.tags && hotel.tags.length > 0 && (
                  <div className="hotel-tags">
                    {hotel.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                )}

                {hotel.amenities && hotel.amenities.length > 0 && (
                  <div className="hotel-amenities-compact">
                    {hotel.amenities.slice(0, 3).map((amenity, index) => (
                      <span key={index} className="amenity-compact">
                        <span className="amenity-icon">✓</span>
                        {amenity}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Section - Pricing & Actions */}
              <div className="hotel-pricing-section">
                {hotel.offer && (
                  <div className="offer-badge">{hotel.offer}</div>
                )}
                <div className="price-details">
                  <div className="price-row">
                    <span className="original-price">₹{hotel.originalPrice.toLocaleString()}</span>
                    <span className="current-price">₹{hotel.currentPrice.toLocaleString()}</span>
                  </div>
                  {hotel.taxes > 0 && (
                    <div className="taxes">+ ₹{hotel.taxes} taxes & fees</div>
                  )}
                  <div className="per-night">Per Night</div>
                </div>
                <div className="hotel-actions">
                  <Link to={`/hotel/${hotel._id || hotel.id}`} className="btn btn-primary">
                    View detail & Book
                  </Link>
                </div>
              </div>
            </div>
          ))}
              </div>
            </div>
          </div>
        </section>
      );
};

export default Hotels;
